import { useList } from "src/system/useList.tsx";
import type { DefaultError, UseQueryOptions } from "@tanstack/react-query";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import type {
  GridFilterModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import type { GridSortModel } from "@mui/x-data-grid/models";

import type { SelectSearchField } from "src/components/Form/Form.types.ts";
import type {
  SelectLabelProp,
  SelectValueProp,
} from "src/system/system.types.ts";
import { useSelectOptions } from "src/system/useSelectOptions.ts";
import { getResourceBySelectValue } from "src/system/getResourceBySelectValue.ts";
import { getSelectFilter } from "./getSelectFilter";
import { getSelectSort } from "src/system/getSelectSort.ts";

type UseSelectProps<Resource extends GridValidRowModel = GridValidRowModel> = {
  resource: string;
  labelProp?: SelectLabelProp<Resource>;
  search?: string;
  locale?: string;
  include?: string[];
  searchField?: SelectSearchField | { (search: string): SelectSearchField };
  onQueryFn?: () => void;
  valueProp?: SelectValueProp;
  filter?: GridFilterModel;
  sort?: GridSortModel;
  disablePagination?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Resource>,
      DefaultError,
      ValidGetListResponse<Resource>,
      any
    >,
    "queryKey" | "queryFn"
  >;
};

export const useSelect = <
  Resource extends GridValidRowModel = GridValidRowModel,
>(
  props: UseSelectProps<Resource>,
) => {
  const {
    resource,
    labelProp,
    valueProp,
    disablePagination = false,
    filter: filterProp,
    sort: sortProp,
    searchField,
    onQueryFn,
    search,
    locale,
    include,
    queryOptions,
  } = props;
  const filter = getSelectFilter({
    labelProp,
    search,
    searchField,
    filter: filterProp,
  });
  const sort = getSelectSort({
    sort: sortProp,
    resource,
  });

  const { data, isLoading, refetch, isFetching, isError } = useList<Resource>({
    resource: resource,
    include,
    locale,
    disablePagination: disablePagination,
    sort,
    onQueryFn,
    filter: filter,
    queryOptions: {
      refetchOnMount: true,
      staleTime: search ? 0 : Infinity,
      ...queryOptions,
    },
  });

  const resources = data?.data || [];

  const options = useSelectOptions({
    valueProp,
    labelProp,
    resources,
  });

  const fetchOnOpen = (open: boolean) => {
    if (open) {
      refetch();
    }
  };

  const getResourceByValue = (value: any) => {
    return getResourceBySelectValue({
      resources,
      value,
      valueProp,
    });
  };

  return {
    options,
    isLoading,
    refetch,
    isFetching,
    isError,
    data,
    getResourceByValue,
    fetchOnOpen,
  };
};
